import htmlToText from "../node/html-to-text";
import type {
  FormItemNode,
  IDocumentData,
  IDocumentNode,
  IFormNode,
  ISwiperContainerNode,
  PartialNode,
} from "../node/type";
import { findNodeByPredicate } from "../node/util";
import type { Campaign, CampaignType } from "../types/campaign";
import type { Submission } from "../types/form";
import { getCampaignName } from "./campaign";

export const convertSubmissionToString = (
  submission: Submission | undefined | null
): string => {
  if (!submission) {
    return "-";
  }

  if (typeof submission === "string") {
    if (submission === "") {
      return "-";
    }
    return submission;
  }

  if (typeof submission === "number") {
    return submission.toString();
  }

  if (Array.isArray(submission)) {
    if (submission.length === 0) {
      return "-";
    }

    return submission.join(", ");
  }

  switch (submission.type) {
    case "IMAGE_CHOICE_SUBMISSION": {
      if (submission.choices.length === 0) {
        return "-";
      }
      return submission.choices.map((choice) => choice.label).join(", ");
    }
    case "SIMPLE_CHOICE_SUBMISSION": {
      if (submission.choices.length === 0) {
        return "-";
      }
      return submission.choices.map((choice) => choice.label).join(", ");
    }
    case "FILE_SUBMISSION": {
      return submission.files.map((file) => file.url).join("\n");
    }
  }
};

export const hasSubmission = <T extends PartialNode<FormItemNode>>(
  node: T
): node is Exclude<
  T,
  { type?: "FORM_DIVIDER" | "FORM_TEXT" | "FORM_IMAGE_VIDEO" }
> => {
  if (!node.type) {
    throw new Error("node.type is required");
  }

  switch (node.type) {
    case "FORM_TEXT":
    case "FORM_DIVIDER":
    case "FORM_IMAGE_VIDEO":
      return false;
    case "FORM_INPUT":
    case "FORM_CHOICE":
    case "FORM_PRIVACY_POLICY":
    case "FORM_IMAGE_CHOICE":
    case "FORM_DROPDOWN":
    case "FORM_RATING_SCALE":
    case "FORM_TEXTAREA":
    case "FORM_HIDDEN_FIELD":
    case "FORM_NPS":
    case "FORM_SEAN_ELLIS":
    case "FORM_FILE_UPLOADER":
      return true;
  }
};

export const getFormNodefromCampaign = (
  campaign: Campaign | null
): IFormNode | null => {
  if (!campaign || campaign.type === "INSTAGRAM") {
    return null;
  }
  let result: IFormNode | null = null;

  campaign.data.documents.forEach((doc) => {
    if (result) {
      return;
    }

    result = findNodeByPredicate<IFormNode>(
      doc,
      (node): node is IFormNode => node.type === "FORM"
    );
  });

  return result;
};

export const getSwiperContainerNodefromDocumentData = (
  documentData: IDocumentData | null
): ISwiperContainerNode | null => {
  let result: ISwiperContainerNode | null = null;

  documentData?.documents.forEach((doc) => {
    if (result) {
      return;
    }

    result = findNodeByPredicate<ISwiperContainerNode>(
      doc,
      (node): node is ISwiperContainerNode => node.type === "SWIPER_CONTAINER"
    );
  });

  return result;
};

export const getSwiperContainerNodefromDocument = (
  document: IDocumentNode
): ISwiperContainerNode | null => {
  let result: ISwiperContainerNode | null = null;

  result = findNodeByPredicate<ISwiperContainerNode>(
    document,
    (node): node is ISwiperContainerNode => node.type === "SWIPER_CONTAINER"
  );

  return result;
};

/** 폼의 이미지 선택형에서 레이블 정보를 인덱스 기반으로 뽑아오는 유틸 함수입니다 */
export const getImageLabelFromIndex = (index: number) => {
  return `이미지 ${index + 1}`;
};

export const extractFormChoiceValue = (value: string) => {
  const splittedValue = value.split("#");
  return {
    label: splittedValue[0] ?? value,
    id: splittedValue[1] ?? "",
  };
};

export const generateFormDataName = (formItem: PartialNode<FormItemNode>) => {
  return `${htmlToText(formItem.label)}#${formItem.id}#${formItem.type}`;
};

export const generateHiddenFieldDataName = (field: string) => {
  return `${field}##hidden`;
};

export const extractFormDataName = (formDataName: string) => {
  const splittedValue = formDataName.split("#");
  const length = splittedValue.length;

  return length > 3
    ? // label에 #이 포함되어 있는 경우
      {
        label: splittedValue.slice(0, length - 2).join("#"),
        id: splittedValue[length - 2] ?? null,
        type: splittedValue[length - 1] ?? null,
      }
    : {
        label: splittedValue[0] ?? formDataName,
        id: splittedValue[1] ?? null,
        type: splittedValue[2] ?? null,
      };
};

export const setMapIfUnique = <K, V>(
  map: Map<K, V | null>,
  key: K,
  value: V
) => {
  map.set(key, map.has(key) ? null : value);
};

export const getHiddenFieldId = (field: string) => {
  return `${field}-hidden`;
};

export const generateFormLabelIdMap = (formNode: IFormNode) => {
  return formNode.children.reduce<Map<string, string | null>>(
    (labelIdMap, child) => {
      if (child.type === "FORM_HIDDEN_FIELD") {
        child.fields.forEach((field) => {
          setMapIfUnique(labelIdMap, field, getHiddenFieldId(field));
        });
        return labelIdMap;
      }

      setMapIfUnique(labelIdMap, htmlToText(child.label), child.id);
      return labelIdMap;
    },
    new Map()
  );
};

export const generateFormIdLabelMap = (formNode: IFormNode) => {
  return formNode.children.reduce<Map<string, string>>((idLabelMap, child) => {
    idLabelMap.set(child.id, htmlToText(child.label));
    if (
      child.type === "FORM_CHOICE" ||
      child.type === "FORM_DROPDOWN" ||
      child.type === "FORM_SEAN_ELLIS"
    ) {
      child.items.forEach((item) => {
        idLabelMap.set(item.id, htmlToText(item.label));
      });
    }

    if (child.type === "FORM_HIDDEN_FIELD") {
      child.fields.forEach((field) => {
        idLabelMap.set(getHiddenFieldId(field), field);
      });
    }

    if (child.type === "FORM_IMAGE_CHOICE") {
      child.items.forEach((item, idx) => {
        idLabelMap.set(item.id, getImageLabelFromIndex(idx));
      });
    }
    return idLabelMap;
  }, new Map());
};

export const sanitizeSubmission = <T extends Submission>(
  submission: T,
  idLabelMap: Map<string, string>
): T => {
  if (
    typeof submission === "object" &&
    "type" in submission &&
    "choices" in submission
  ) {
    return {
      ...submission,
      choices: submission.choices
        .filter((choice) => idLabelMap.get(choice.id))
        .map((choice) => ({
          ...choice,
          label: idLabelMap.get(choice.id) ?? choice.label,
        })),
    };
  }
  return submission;
};

export const generateIdBasedSubmissionRecords = (
  submisisonRecords: Record<string, Submission>[],
  formNode: IFormNode
) => {
  const idLabelMap = generateFormIdLabelMap(formNode);
  const labelIdMap = generateFormLabelIdMap(formNode);

  return submisisonRecords.reduce<Record<string, Submission>[]>(
    (idBasedSubmissionRecords, submissionRecord) => {
      const record: Record<string, Submission> = {};
      Object.entries(submissionRecord).forEach(([key, value]) => {
        if (key === "pk" || key === "sk") {
          record[key] = value;
          return;
        }

        const { label, id, type } = extractFormDataName(key);

        if (!id) {
          const alternativeId =
            type === "hidden" ? getHiddenFieldId(label) : labelIdMap.get(label);
          if (alternativeId) {
            record[alternativeId] = sanitizeSubmission(value, idLabelMap);
          }
          return;
        }

        if (idLabelMap.has(id)) {
          record[id] = sanitizeSubmission(value, idLabelMap);
        }
      });

      idBasedSubmissionRecords.push(record);
      return idBasedSubmissionRecords;
    },
    []
  );
};

export const getFormName = (campaignType: CampaignType) => {
  if (campaignType === "FORM_SURVEY") {
    return "설문내용";
  }

  return getCampaignName(campaignType);
};

export const getFormItemIdLabelArr = (formNode: IFormNode) => {
  return formNode.children.filter(hasSubmission).flatMap((child) => {
    if (child.type === "FORM_HIDDEN_FIELD") {
      return child.fields.map((field) => {
        return { label: field, id: getHiddenFieldId(field), nodeId: child.id };
      });
    }
    return { label: htmlToText(child.label), id: child.id, nodeId: child.id };
  });
};

/**
 * 파일 크기를 바이트로 입력 받아
 * B, KB, MB, GB, TB 단위로 환산하여 문자열로 반환합니다.
 *
 * @param {number} sizeBytes - 파일 크기(바이트)
 * @return {string} 변환된 파일 크기 문자열
 */
export const convertSize = (sizeBytes: number): string => {
  if (sizeBytes === 0) {
    return "0 B";
  }

  const units = ["B", "KB", "MB", "GB", "TB"];
  let index = 0;

  while (sizeBytes >= 1024 && index < units.length - 1) {
    sizeBytes /= 1024;
    index++;
  }

  // 소수점 둘째 자리까지 표시
  return sizeBytes.toFixed(2) + " " + units[index];
};
